import { useState } from "react";
import { Check, Link2, Lock, Minus, Plus, Unlock, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import type {
  PlanningBlock,
  PlanningCard,
  PlanningCell,
  PlanningPickIntent,
  PlanningPickup,
  PlanningSlotRef,
} from "../../types/planning";
import { canMove, canUnassign, cardCode } from "../../utils/planningBoard";
import { PlanningCardView, EMPTY } from "./PlanningCardView";
import HandlerBubble from "../handler/HandlerBubble";
import { partnerStripe } from "../../constants/partnerColors";

/**
 * One cell of the grid — a truck, a day, and a position within that day.
 *
 * Exactly three things a slot can be, and they are mutually exclusive by
 * construction rather than by convention:
 *
 *   · holding a container — the plan
 *   · empty              — capacity, and somewhere a card can land
 *   · marked occupied    — empty, and deliberately not available
 *
 * The third is what an empty slot cannot say for itself. A truck in for its
 * inspection on Wednesday morning has an empty slot that is not free, and a
 * board with no way to state that leaves the next dispatcher planning into it.
 * A blocked slot refuses drops and has to be released before it will take one —
 * quietly overwriting the block would erase a decision with no record it was
 * ever made.
 *
 * ── Landing a card ───────────────────────────────────────────────────────────
 *
 * Two ways in, both reaching the same handler. Drag-and-drop for a pointer, and
 * tap-to-place for everything else: HTML5 drag events do not exist on touch, and
 * this board is used on a tablet as often as at a desk. So an empty slot shows a
 * "place here" affordance whenever a card is picked up, and that affordance is
 * the primary interaction on a phone rather than a fallback for one.
 *
 * ── A container over several slots ───────────────────────────────────────────
 *
 * A container may hold more than one cell of its truck's week. Its earliest cell
 * draws the full card; every other one draws a slim "continued" cell, so the
 * capacity reads as taken without the send button appearing three times. While
 * a container's slots are being edited, empty cells on *its* truck offer
 * "Add slot" and nothing else — other trucks offer nothing, because a container
 * never spans two.
 */

interface Props {
  slotRef: PlanningSlotRef;
  /** The container in this cell, and which of its cells this is. */
  entry: { card: PlanningCard; index: number; total: number } | null;
  block: PlanningBlock | null;
  /** The card the dispatcher is holding, if any — decides what an empty slot offers. */
  picked: PlanningPickup | null;
  /** The container whose slots are being edited, if any. */
  editing: PlanningCard | null;
  busyId: string | null;

  onPick: (pickup: PlanningPickup, intent?: PlanningPickIntent) => void;
  onDrop: (slotRef: PlanningSlotRef) => void;
  onSend: (card: PlanningCard) => void;
  onUnsend: (card: PlanningCard) => void;
  onUnassign: (card: PlanningCard) => void;
  onOpen: (card: PlanningCard) => void;
  /** The one card opened out to show its stops, if it is on this board. */
  mapOpenId: string | null;
  onToggleMap: (card: PlanningCard) => void;
  onBlock: (slotRef: PlanningSlotRef, reason?: string) => void;
  onRelease: (block: PlanningBlock) => void;
  onEditSlots: (card: PlanningCard) => void;
  onAddSlot: (slotRef: PlanningSlotRef) => void;
  onRemoveSlot: (card: PlanningCard, cell: PlanningCell) => void;
  /**
   * Is this container inside the handler filter the toolbar is set to?
   *
   * `false` collapses it to a grey tile instead of hiding it — see the branch
   * below, which is the one genuinely non-obvious rule on this screen.
   */
  inScope?: boolean;
  /** Resolves the handler's initials when the card carries only an id. */
  handlerName?: (id: string | null | undefined) => string;
}

export function PlanningSlot({
  slotRef,
  entry,
  block,
  picked,
  editing,
  busyId,
  onPick,
  onDrop,
  onSend,
  onUnsend,
  onUnassign,
  onOpen,
  mapOpenId,
  onToggleMap,
  onBlock,
  onRelease,
  onEditSlots,
  onAddSlot,
  onRemoveSlot,
  inScope = true,
  handlerName,
}: Props) {
  const { t } = useTranslation("calendar");
  const [over, setOver] = useState(false);
  /** The inline "why is this held" form, open only on the cell being marked. */
  const [marking, setMarking] = useState(false);
  const [reason, setReason] = useState("");

  /**
   * Whether this cell will accept what is being held.
   *
   * A blocked slot never will. A slot holding a container will — the server
   * swaps the two, which is the commonest thing a dispatcher does here — except
   * when the card being held is the one already in it, where there is nothing to
   * do and a hover highlight would promise otherwise.
   */
  const card = entry?.card ?? null;
  const cell: PlanningCell = { date: slotRef.date, slot: slotRef.slot };

  /*
   * A container spread over several slots is never swapped (the server refuses
   * it), so none of its cells promises a drop.
   */
  const accepts =
    Boolean(picked) &&
    !block &&
    picked?.card.id !== card?.id &&
    !(entry && entry.total > 1);

  /** Editing this container's slots, and this cell is on its truck. */
  const editingHere = Boolean(editing && editing.vehicleId === slotRef.vehicleId);
  const editingThis = Boolean(editing && card && editing.id === card.id);

  const frame = (children: React.ReactNode) => (
    <div
      onDragOver={(event) => {
        if (!accepts) return;
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        if (accepts) onDrop(slotRef);
      }}
      className={`h-full rounded-lg transition ${over ? "bg-secondary-50 ring-2 ring-secondary-400" : ""}`}
    >
      {children}
    </div>
  );

  if (block) {
    /**
     * Held back — and it has to *look* held back.
     *
     * The first version of this cell hatched two greys three shades apart and
     * put a small grey pill in the middle, which is very close to what an empty
     * slot already looks like: a pale cell with a small grey button. Across a
     * twenty-eight cell truck-week the two were not distinguishable at a glance,
     * which defeats the entire purpose — the block exists so that empty capacity
     * and withheld capacity cannot be confused.
     *
     * So it is amber, hatched against a tinted ground rather than against white,
     * its border is solid where every other cell's is dashed, and the word is
     * set bold and uppercase. Amber rather than red: the slot is unavailable,
     * not wrong, and red on a planning grid is where a dispatcher looks for
     * problems.
     */
    return frame(
      <div
        title={block.reason || t("planning.slot.occupied")}
        className="flex min-h-[92px] flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-amber-300 p-2 text-center"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, #FEF3C7, #FEF3C7 7px, #FADFA1 7px, #FADFA1 14px)",
        }}
      >
        <span className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-amber-900">
          <Lock className="h-3.5 w-3.5" />
          {t("planning.slot.occupied")}
        </span>
        {/*
          Why it is held, where anybody said.
          The reason is the whole difference between "not available" and "not
          available *because the truck is in for its inspection*", and the second
          is what stops the next dispatcher releasing it just to find out.
        */}
        {block.reason && (
          <span className="line-clamp-2 text-[10px] font-medium text-amber-800">
            {block.reason}
          </span>
        )}
        <button
          type="button"
          onClick={() => onRelease(block)}
          className="flex items-center gap-1 rounded-full border border-amber-400 bg-white px-2 py-0.5 text-[10px] font-semibold text-amber-900 transition hover:bg-amber-50"
        >
          <Unlock className="h-3 w-3" />
          {t("planning.slot.release")}
        </button>
      </div>,
    );
  }

  /**
   * ── Somebody else's job, under a handler filter ───────────────────────────
   *
   * Collapsed to a small grey tile, and **not hidden**. This is the one rule on
   * this screen that is worth being emphatic about, because hiding would be the
   * obvious thing to do and it would be a bug with a truck in it.
   *
   * A truck is a shared resource. Four slots a day, and whoever is looking at
   * the board is deciding what to put in them. If "Mine" removed a colleague's
   * job from a cell, that cell would read as free — to the dispatcher *and* to
   * the free-slot count beside the day — and the next container dropped into it
   * would be the second one promised to a truck that can only do one. The
   * refusal would come from the server, after the client had already drawn it,
   * which is precisely the failure the board's own broadcast exists to prevent.
   *
   * So the cell stays occupied and says the two things the dispatcher needs:
   * the container number, and whose it is. Both are the whole point — "that
   * slot is taken, and Anna is the one to ask about it" is actionable, where an
   * anonymous grey block is just an obstacle.
   *
   * Everything else about the tile is deliberately absent. It is not draggable,
   * carries no actions and does not open: it is not this dispatcher's work, and
   * a filter is a lens rather than a lock — the tile is a statement of
   * occupancy, and anyone who actually wants the job switches to Everyone.
   *
   * The counts are untouched by any of this. They are computed in the page from
   * every placement the board returned, which is why the grid is never filtered
   * server-side at all.
   */
  if (entry && card && !inScope) {
    const initials = handlerName?.(card.handler?.id) ?? "";
    return frame(
      <div
        title={`${card.lineNumber ?? card.containerNumber ?? ""}${
          initials ? ` · ${initials}` : ""
        }`}
        className="flex h-full min-h-[2.25rem] items-center gap-1.5 rounded-md border border-[#E6E3D9] bg-[#F1EFE8] px-2 py-1"
      >
        <HandlerBubble handler={card.handler} size="sm" silent />
        <span className="truncate font-mono text-[10.5px] font-bold text-[#6F6E68]">
          {card.lineNumber ?? card.containerNumber ?? EMPTY}
        </span>
        {/* The slot is still accounted for even when a container holds several
            — the second cell of somebody else's two-cell job is as taken as
            the first. */}
        {entry.total > 1 && (
          <span className="ml-auto shrink-0 text-[9.5px] font-semibold text-[#8B8B85]">
            {entry.index}/{entry.total}
          </span>
        )}
      </div>,
    );
  }

  if (entry && card && entry.index === 1) {
    return frame(
      <PlanningCardView
        card={card}
        context="slot"
        slotCount={entry.total}
        picked={picked?.card.id === card.id}
        editingSlots={editingThis}
        busy={busyId === card.id}
        onPick={onPick}
        onSend={onSend}
        onUnsend={onUnsend}
        onUnassign={onUnassign}
        onOpen={onOpen}
        mapOpen={mapOpenId === card.id}
        onToggleMap={onToggleMap}
        onEditSlots={onEditSlots}
        /*
         * Freeing the last remaining slot is offered only where the server
         * allows it — on a started container, which has no unassign button to
         * reach for instead. Below that the refusal is deliberate and names
         * the button to press (see `removeSlot` in the planning controller),
         * so offering it here would only produce a 409.
         */
        onFreeSlot={
          entry.total > 1 || !canUnassign(card)
            ? () => onRemoveSlot(card, cell)
            : undefined
        }
      />,
    );
  }

  if (entry && card) {
    /*
     * One of a container's further slots.
     *
     * Deliberately slim: it has to read as *taken* at a glance across a
     * truck-week, and as the same job as the full card — hence the container's
     * own number and city, and the link glyph — without repeating the send row.
     */
    const movable = canMove(card);
    // The same stripe as the full card, so a multi-slot job reads as one thing.
    const stripe = partnerStripe(card.orderClientPartner);
    return frame(
      <div
        className={`flex min-h-[92px] flex-col rounded-lg border border-l-4 border-secondary-200 border-l-secondary-400 bg-secondary-50/60 p-2 transition ${
          editingThis ? "ring-2 ring-secondary-300" : ""
        } ${busyId === card.id ? "opacity-60" : ""}`}
        style={stripe ? { borderLeftColor: stripe } : undefined}
      >
        <div className="flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-wide text-secondary-700">
          <Link2 className="h-3 w-3" />
          {t("planning.slot.continued", { index: entry.index, total: entry.total })}
        </div>
        <div className="mt-1 truncate text-[12px] font-bold leading-tight text-dark-900">
          {card.city || t("planning.card.noCity")}
        </div>
        <div className="truncate font-mono text-[10.5px] font-semibold text-gray-600">
          {cardCode(card)}
        </div>
        <div className="mt-auto flex items-center justify-end gap-1 pt-1.5">
          <button
            type="button"
            onClick={() => onOpen(card)}
            className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-gray-500 transition hover:border-secondary-300 hover:text-secondary-700"
          >
            {t("planning.card.open")}
          </button>
          {movable && (
            <button
              type="button"
              disabled={busyId === card.id}
              onClick={() => onRemoveSlot(card, cell)}
              title={t("planning.slot.freeHint")}
              className="flex items-center gap-1 rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-gray-500 transition hover:border-orange-300 hover:text-orange-700 disabled:opacity-50"
            >
              <Minus className="h-3 w-3" />
              {t("planning.slot.free")}
            </button>
          )}
        </div>
      </div>,
    );
  }

  /*
   * An empty cell while a container's slots are being edited.
   *
   * On that container's truck it offers exactly one thing — take this slot too.
   * On every other truck it offers nothing at all, not even "Mark occupied": a
   * container never spans two trucks, and a board that lit up other rows would
   * suggest it could.
   */
  if (editing && !block) {
    return frame(
      <div
        className={`flex min-h-[92px] flex-col items-center justify-center rounded-lg border border-dashed p-2 transition ${
          editingHere ? "border-secondary-400 bg-secondary-50/40" : "border-gray-100 opacity-50"
        }`}
      >
        {editingHere ? (
          <button
            type="button"
            disabled={busyId === editing.id}
            onClick={() => onAddSlot(slotRef)}
            className="flex items-center gap-1 rounded-full bg-secondary-600 px-3 py-1 text-[11px] font-semibold text-white transition hover:bg-secondary-700 disabled:opacity-50"
          >
            <Plus className="h-3 w-3" />
            {t("planning.slot.addSlot")}
          </button>
        ) : null}
      </div>,
    );
  }

  return frame(
    <div
      className={`flex min-h-[92px] flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-2 transition ${
        accepts ? "border-secondary-400 bg-secondary-50/40" : "border-gray-200"
      }`}
    >
      {accepts ? (
        /*
         * The tap target that makes the board work without a mouse. Shown only
         * while a card is held, so an idle board is not covered in buttons.
         */
        <button
          type="button"
          onClick={() => onDrop(slotRef)}
          className="flex items-center gap-1 rounded-full bg-secondary-600 px-3 py-1 text-[11px] font-semibold text-white transition hover:bg-secondary-700"
        >
          <Plus className="h-3 w-3" />
          {t("planning.slot.placeHere")}
        </button>
      ) : marking ? (
        /*
         * Why the slot is being held, asked at the moment it is held.
         *
         * Inline rather than in a modal, and optional rather than required. A
         * dispatcher blocking four slots down a row should not be interrupted
         * by four dialogs, and plenty of blocks have nothing worth typing — but
         * the ones that do ("in for inspection", "holding for Delamode") are
         * exactly the ones the next person needs, and there was previously no
         * way at all to say them.
         */
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onBlock(slotRef, reason.trim() || undefined);
            setMarking(false);
            setReason("");
          }}
          className="flex w-full flex-col gap-1.5"
        >
          <input
            autoFocus
            value={reason}
            maxLength={200}
            onChange={(event) => setReason(event.target.value)}
            onKeyDown={(event) => {
              // Swallowed, so Escape closes this form rather than reaching the
              // page and putting down a card the dispatcher is still holding.
              if (event.key === "Escape") {
                event.stopPropagation();
                setMarking(false);
                setReason("");
              }
            }}
            placeholder={t("planning.slot.reasonPlaceholder")}
            className="w-full rounded border border-amber-300 px-1.5 py-1 text-[11px] focus:border-amber-500 focus:outline-none"
          />
          <div className="flex gap-1">
            <button
              type="submit"
              className="flex flex-1 items-center justify-center gap-1 rounded bg-amber-500 px-2 py-1 text-[10px] font-bold text-white transition hover:bg-amber-600"
            >
              <Check className="h-3 w-3" />
              {t("planning.slot.confirmOccupied")}
            </button>
            <button
              type="button"
              onClick={() => {
                setMarking(false);
                setReason("");
              }}
              className="rounded border border-gray-200 px-1.5 py-1 text-gray-500 transition hover:bg-gray-50"
              aria-label={t("planning.slot.cancel")}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </form>
      ) : (
        <>
          <span className="text-[11px] text-gray-300">
            {t("planning.slot.empty")}
          </span>
          <button
            type="button"
            onClick={() => setMarking(true)}
            className="flex items-center gap-1 rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-gray-500 transition hover:border-amber-300 hover:bg-amber-50 hover:text-amber-800"
          >
            <Lock className="h-3 w-3" />
            {t("planning.slot.markOccupied")}
          </button>
        </>
      )}
    </div>,
  );
}
